import { useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Loader2, Calendar, Printer, FileText, StickyNote } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

const DAYS = ["الأحد", "الاثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];
const DAY_COLORS = [
  "bg-blue-50 border-blue-200 dark:bg-blue-950/30 dark:border-blue-800",
  "bg-emerald-50 border-emerald-200 dark:bg-emerald-950/30 dark:border-emerald-800",
  "bg-purple-50 border-purple-200 dark:bg-purple-950/30 dark:border-purple-800",
  "bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800",
  "bg-rose-50 border-rose-200 dark:bg-rose-950/30 dark:border-rose-800",
  "bg-gray-50 border-gray-200 dark:bg-gray-950/30 dark:border-gray-800",
  "bg-cyan-50 border-cyan-200 dark:bg-cyan-950/30 dark:border-cyan-800",
];

interface Props {
  classId: string | null;
  childName?: string;
}

export default function ParentWeeklySchedule({ classId, childName }: Props) {
  const printRef = useRef<HTMLDivElement>(null);
  const todayIdx = new Date().getDay();

  const { data: schedules, isLoading } = useQuery({
    queryKey: ["parent-class-schedules", classId],
    queryFn: async () => {
      if (!classId) return [];
      const { data } = await supabase
        .from("class_schedules")
        .select("*, staff(full_name)")
        .eq("class_id", classId)
        .order("day_of_week")
        .order("start_time");
      return data || [];
    },
    enabled: !!classId,
  });

  // Fetch linked assignments
  const assignmentIds = [...new Set((schedules || []).map((s: any) => s.assignment_id).filter(Boolean))];
  const { data: linkedAssignments } = useQuery({
    queryKey: ["parent-schedule-assignments", assignmentIds],
    queryFn: async () => {
      if (!assignmentIds.length) return [];
      const { data } = await supabase
        .from("assignments")
        .select("id, title, due_date")
        .in("id", assignmentIds);
      return data || [];
    },
    enabled: assignmentIds.length > 0,
  });

  const grouped = (schedules || []).reduce((acc: Record<number, any[]>, s: any) => {
    (acc[s.day_of_week] = acc[s.day_of_week] || []).push(s);
    return acc;
  }, {});

  const handlePrint = () => {
    const content = printRef.current;
    if (!content) return;
    const win = window.open("", "_blank");
    if (!win) return;
    win.document.write(`<!DOCTYPE html><html dir="rtl"><head><meta charset="utf-8"><title>الجدول الأسبوعي${childName ? ` - ${childName}` : ""}</title>
    <style>
      body { font-family: 'Segoe UI', Tahoma, sans-serif; padding: 20px; direction: rtl; }
      h2 { text-align: center; margin-bottom: 20px; }
      .day-title { font-weight: bold; margin: 16px 0 8px; font-size: 16px; border-bottom: 2px solid #333; padding-bottom: 4px; }
      .slot { display: flex; gap: 16px; padding: 8px 12px; border: 1px solid #ddd; border-radius: 6px; margin-bottom: 6px; }
      .today-highlight { background: #fffbeb; border-color: #f59e0b; }
    </style></head><body>
    <h2>الجدول الأسبوعي${childName ? ` - ${childName}` : ""}</h2>${content.innerHTML}
    </body></html>`);
    win.document.close();
    win.print();
  };

  if (!classId) {
    return (
      <Card>
        <CardContent className="py-12 text-center">
          <Calendar className="w-10 h-10 text-muted-foreground/30 mx-auto mb-2" />
          <p className="text-muted-foreground text-sm">لا يوجد فصل مرتبط بالطفل</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between gap-4 space-y-0">
        <CardTitle className="flex items-center gap-2 text-lg">
          <Calendar className="w-5 h-5 text-primary" />
          الجدول الأسبوعي {childName && <span className="text-sm font-normal text-muted-foreground">- {childName}</span>}
        </CardTitle>
        {Object.keys(grouped).length > 0 && (
          <Button variant="outline" size="sm" className="gap-1" onClick={handlePrint}>
            <Printer className="w-4 h-4" /> طباعة
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
        ) : Object.keys(grouped).length === 0 ? (
          <p className="text-center text-muted-foreground py-8">لم يتم إعداد الجدول الأسبوعي بعد</p>
        ) : (
          <div className="space-y-4" ref={printRef}>
            {DAYS.map((dayName, dayIdx) => {
              const slots = grouped[dayIdx];
              if (!slots?.length) return null;
              const isToday = dayIdx === todayIdx;
              return (
                <div key={dayIdx}>
                  <h4 className={`font-semibold text-sm mb-2 day-title ${isToday ? "text-primary" : "text-muted-foreground"}`}>
                    {dayName} {isToday && <Badge className="mr-2 text-[10px]">اليوم</Badge>}
                  </h4>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
                    {slots.map((slot: any) => {
                      const assignment = (linkedAssignments || []).find((a: any) => a.id === slot.assignment_id);
                      return (
                        <div key={slot.id} className={`p-3 rounded-lg border slot ${isToday ? "ring-2 ring-primary/20 today-highlight" : ""} ${DAY_COLORS[dayIdx]}`}>
                          <div>
                            <p className="font-medium text-sm">{slot.subject}</p>
                            <p className="text-xs text-muted-foreground mt-0.5">
                              {slot.start_time?.slice(0, 5)} - {slot.end_time?.slice(0, 5)}
                            </p>
                            {(slot as any).staff?.full_name && (
                              <p className="text-xs text-muted-foreground">{(slot as any).staff.full_name}</p>
                            )}
                            {slot.room && <p className="text-xs text-muted-foreground">📍 {slot.room}</p>}
                            {slot.notes && (
                              <p className="text-xs text-muted-foreground mt-1 flex items-start gap-1">
                                <StickyNote className="w-3 h-3 mt-0.5 shrink-0" /> {slot.notes}
                              </p>
                            )}
                            {assignment && (
                              <Badge variant="secondary" className="mt-1.5 text-[10px] gap-1">
                                <FileText className="w-3 h-3" /> واجب: {assignment.title}
                              </Badge>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
